const getEnvValue = (name) => (typeof window !== 'undefined'
    && window[name]
    && !window[name].includes('PLACEHOLDER')
    ? window[name]
    : '');

const API_TOKEN_QUERY_KEY = 'token';
const API_TOKEN_STORAGE_KEY = 'mini-tokyo-3d-api-token';

function isBrowser() {
    return typeof window !== 'undefined';
}

function persistApiTokenFromUrl() {
    if (!isBrowser() || !window.location || typeof URLSearchParams === 'undefined') {
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get(API_TOKEN_QUERY_KEY);

    if (!token) {
        return;
    }

    try {
        window.sessionStorage.setItem(API_TOKEN_STORAGE_KEY, token);
    } catch (error) {
        console.warn('Failed to store API token in sessionStorage.', error);
    }
}

export function getApiAuthorizationToken() {
    if (!isBrowser()) {
        return '';
    }

    persistApiTokenFromUrl();

    try {
        return window.sessionStorage.getItem(API_TOKEN_STORAGE_KEY) || '';
    } catch (error) {
        console.warn('Failed to read API token from sessionStorage.', error);
        return '';
    }
}

export function buildApiRequestHeaders(headers = {}) {
    const token = getApiAuthorizationToken();

    if (!token) {
        return headers;
    }

    return {
        ...headers,
        Authorization: `Bearer ${token}`
    };
}

persistApiTokenFromUrl();

const configs = {

    // Standing duration at origin and destination in milliseconds
    standingDuration: 60000,

    // Minimum standing duration in milliseconds
    minStandingDuration: 30000,

    // Interval of refreshing train positions in milliseconds
    trainRefreshInterval: 60000,

    // All train positions will be refreshed if the screen has been inactive for this duration
    refreshTimeout: 10000,

    // Interval of checking train and bus positions based on real-time data in milliseconds
    realtimeCheckInterval: 15000,

    // Maximum train speed in km/h
    maxSpeedKMPH: 80,

    // Train acceleration in km/h/s
    accelerationKMPHPS: 3,

    // Maximum train speed in km/ms
    get maxSpeed() {
        return configs.maxSpeedKMPH / 3600000;
    },

    // Train acceleration in km/ms^2
    get acceleration() {
        return configs.accelerationKMPHPS / 3600000000;
    },

    // Time required to reach maximum train speed in milliseconds
    get maxAccelerationTime() {
        return configs.maxSpeed / configs.acceleration;
    },

    // Distance required to reach maximum train speed in kilometers
    get maxAccDistance() {
        return configs.maxAccelerationTime * configs.maxSpeed / 2;
    },

    // Maximum flight speed in km/h
    maxFlightSpeedKMPH: 500,

    // Flight acceleration in km/h/s
    flightAccelerationKMPHPS: 12,

    // Maximum flight speed in km/ms
    get maxFlightSpeed() {
        return configs.maxFlightSpeedKMPH / 3600000;
    },

    // Flight acceleration in km/ms^2
    get flightAcceleration() {
        return configs.flightAccelerationKMPHPS / 3600000000;
    },

    // Maximum bus speed in km/h
    maxBusSpeedKMPH: 30,

    // Bus acceleration in km/h/s
    busAccelerationKMPHPS: 3,

    // Maximum bus speed in km/ms
    get maxBusSpeed() {
        return configs.maxBusSpeedKMPH / 3600000;
    },

    // Bus acceleration in km/ms^2
    get busAcceleration() {
        return configs.busAccelerationKMPHPS / 3600000000;
    },

    // Time required to reach maximum bus speed in milliseconds
    get maxBusAccelerationTime() {
        return configs.maxBusSpeed / configs.busAcceleration;
    },

    // Distance required to reach maximum bus speed in kilometers
    get maxBusAccDistance() {
        return configs.maxBusAccelerationTime * configs.maxBusSpeed / 2;
    },

    // Delay in milliseconds for minimizing precision error
    minDelay: 25000,

    // Minimum flight interval in milliseconds
    minFlightInterval: 90000,

    // Time allotted for transitions to complete
    transitionDuration: 300,

    // Fade duration when an object is added or removed
    fadeDuration: 1000,

    // Origin of coordinates (around Tokyo station)
    defaultCenter: [139.7670, 35.6814],

    // Default zoom level
    defaultZoom: 14,

    // Default bearing (rotation) of the map
    defaultBearing: 0,

    // Default pitch in degrees
    defaultPitch: 60,

    // Default frame rate for train and aircraft animations in the Eco mode
    defaultEcoFrameRate: 1,

    // Default view mode
    defaultViewMode: 'ground',

    // Default tracking mode
    defaultTrackingMode: 'position',

    // Default clock mode
    defaultClockMode: 'realtime',

    // Default clock mode
    defaultEcoMode: 'normal',

    // API URL
    apiUrl: {

        // ODPT URL
        odpt: 'https://api.odpt.org/api/v4/',

        // Challenge 2024 URL
        challenge2024: 'https://api-challenge2024.odpt.org/api/v4/',

        // Safie API Gateway URL (ECS Task Definition環境変数から取得)
        safie: getEnvValue('API_GATEWAY_URL'),

        // Safie API Gateway URL (ECS Task Definition環境変数から取得)
        ichimill: getEnvValue('API_GATEWAY_URL_ICHIMILL'),

        // Kouji API Gateway URL (ECS Task Definition環境変数から取得)
        kouji: getEnvValue('API_GATEWAY_URL_KOUJI'),

        // Earthquake API Gateway URL (ECS Task Definition環境変数から取得)
        earthquake: getEnvValue('API_GATEWAY_URL_EARTHQUAKE'),

        // Tsunami API Gateway URL (ECS Task Definition環境変数から取得)
        tsunami: getEnvValue('API_GATEWAY_URL_TSUNAMI'),

        // Typhoon API Gateway URL (ECS Task Definition環境変数から取得)
        typhoon: getEnvValue('API_GATEWAY_URL_TYPHOON'),

        // Eyelink URL (ECS Task Definition環境変数から取得)
        eyelink: getEnvValue('EYELINK_URL')

    },

    // 工事情報サイトのベースURL (ECS Task Definition環境変数から取得)
    koujiSiteUrl: getEnvValue('KOUJI_URL'),

    // Mapbox トークン (ECS Task Definition環境変数から取得)
    mapboxToken: getEnvValue('MAPBOX_TOKEN'),

    // AWS リージョン (ECS Task Definition環境変数から取得)
    awsRegion: getEnvValue('AWS_REGION'),

    // TID URL
    tidUrl: 'https://mini-tokyo.appspot.com/tid',

    // Train information URL
    trainInfoUrl: 'https://mini-tokyo.appspot.com/traininfo',

    // ATIS URL
    atisUrl: 'https://mini-tokyo.appspot.com/atisinfo',

    // Flight URL
    flightUrl: 'https://mini-tokyo.appspot.com/flight',

    // Default data URL
    dataUrl: 'https://minitokyo3d.com/data',

    // Default data sources
    dataSources: [],

    // Route search URL
    searchUrl: 'https://search.minitokyo3d.com/api/v1/routes',

    // Timestamp when the static data was last updated
    lastStaticUpdate: '2024-11-25 15:00:00',

    // String to show in an Mapbox's AttributionControl
    customAttribution: '<a href="https://github.com/nagix/mini-tokyo-3d">© Akihiko Kusanagi</a>',

    // Copyright string
    copyright: '© 2019-2025 Akihiko Kusanagi',

    // Share URL
    shareUrl: 'https://minitokyo3d.com',

    // Supported events
    events: [
        'boxzoomcancel',
        'boxzoomend',
        'boxzoomstart',
        'click',
        'contextmenu',
        'dblclick',
        'drag',
        'dragend',
        'dragstart',
        'error',
        'load',
        'mousedown',
        'mousemove',
        'mouseout',
        'mouseover',
        'mouseup',
        'move',
        'moveend',
        'movestart',
        'pitch',
        'pitchend',
        'pitchstart',
        'resize',
        'rotate',
        'rotateend',
        'rotatestart',
        'touchcancel',
        'touchend',
        'touchmove',
        'touchstart',
        'wheel',
        'zoom',
        'zoomend',
        'zoomstart'
    ],

    // Supported languages
    langs: ['de', 'en', 'es', 'fr', 'ja', 'ko', 'ne', 'pt-BR', 'th', 'zh-Hans', 'zh-Hant'],

    // Safie device settings - 動的データ取得のためのプレースホルダー
    // 実際のデータは loadSafieDevices() 関数で取得される
    safieDevices: [],

    // Safie device update interval in milliseconds (1 minute)
    safieUpdateInterval: 1 * 60 * 1000

};

export default configs;
